import React from 'react';
import { Select, InputNumber, Space } from 'antd';

const HIJRI_MONTHS = [
  { value: '01', label: 'محرم - Muharram' },
  { value: '02', label: 'صفر - Safar' },
  { value: '03', label: 'ربيع الأول - Rabi al-Awwal' },
  { value: '04', label: 'ربيع الثاني - Rabi al-Thani' },
  { value: '05', label: 'جمادى الأولى - Jumada al-Awwal' },
  { value: '06', label: 'جمادى الثانية - Jumada al-Thani' },
  { value: '07', label: 'رجب - Rajab' },
  { value: '08', label: 'شعبان - Shaban' },
  { value: '09', label: 'رمضان - Ramadan' },
  { value: '10', label: 'شوال - Shawwal' },
  { value: '11', label: 'ذو القعدة - Dhu al-Qadah' },
  { value: '12', label: 'ذو الحجة - Dhu al-Hijjah' }
];

interface HijriDateInputProps {
  value?: string;
  onChange?: (value: string | null) => void;
}

const HijriDateInput: React.FC<HijriDateInputProps> = ({ value, onChange }) => {
  // Parse MM-DD format from the external value
  const [internalMonth, setInternalMonth] = React.useState<string | undefined>(
    value ? value.split('-')[0] : undefined
  );
  const [internalDay, setInternalDay] = React.useState<number | undefined>(
    value ? parseInt(value.split('-')[1]) : undefined
  );

  // Update internal state when external value changes
  React.useEffect(() => {
    if (value) {
      const [month, day] = value.split('-');
      setInternalMonth(month);
      setInternalDay(parseInt(day));
    } else {
      // Don't clear internal state when external value is null
      // This allows us to maintain partial selections
    }
  }, [value]);

  // For display purposes, use the internal state
  const month = internalMonth;
  const day = internalDay;

  const handleMonthChange = (newMonth: string | null) => {
    // Update internal state
    setInternalMonth(newMonth || undefined);
    
    if (!newMonth) {
      setInternalDay(undefined); // Also clear day when month is cleared
      onChange?.(null);
      return;
    }
    
    if (!day) {
      // Don't call onChange yet, wait for day to be selected
      return;
    }
    
    // Format as MM-DD
    const formattedValue = `${newMonth}-${day.toString().padStart(2, '0')}`;
    onChange?.(formattedValue);
  };

  const handleDayChange = (newDay: number | null) => {
    // Update internal state
    setInternalDay(newDay || undefined);
    
    if (!newDay) {
      onChange?.(null);
      return;
    }
    
    if (!month) {
      // Don't call onChange yet, wait for month to be selected
      return;
    }
    
    // Format as MM-DD
    const formattedValue = `${month}-${newDay.toString().padStart(2, '0')}`;
    onChange?.(formattedValue);
  };

  return (
    <Space>
      <Select
        style={{ width: 140 }}
        placeholder="Select month"
        options={HIJRI_MONTHS}
        value={month}
        onChange={handleMonthChange}
        allowClear
      />
      <InputNumber
        style={{ width: 70 }}
        min={1}
        max={29}
        value={day}
        onChange={handleDayChange}
        placeholder="Day"
      />
    </Space>
  );
};

export default HijriDateInput; 